/**
 * [PGF Draft Board Refresh v1.1 §F.4 + §G.3] useBoardStyle hook.
 *
 * Single source of truth for "what style should we render right now?"
 * Precedence: user override (localStorage → backend) > league default
 * (draftState) > product default (pgf_standard).
 *
 * Returns:
 *   {
 *     style,             // resolved current style
 *     userOverride,      // this user's per-league override (or null)
 *     leagueDefault,     // commissioner-set default
 *     setLocalStyle,     // (style, asOverride) - called by selector for instant re-render
 *   }
 *
 * Wire-up: consumes `draftState.default_board_style` for the league default
 * (populated by DraftContext STATE_SNAPSHOT which now surfaces the field
 * via GET /api/leagues/{id}). Consumes localStorage for override warm-cache;
 * reconciles with backend GET /users/me/board-preference in background.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { authHeaders } from "../contexts/AuthContext";
import {
  DEFAULT_BOARD_STYLE,
  resolveBoardStyle,
  boardStyleLocalStorageKey,
  isValidBoardStyle,
} from "../utils/boardStyles";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export function useBoardStyle(leagueId, leagueDefaultFromState) {
  // Warm-hydrate from localStorage first to prevent flash of default.
  const initialOverride = (() => {
    if (!leagueId) return null;
    try {
      const v = localStorage.getItem(boardStyleLocalStorageKey(leagueId));
      return isValidBoardStyle(v) ? v : null;
    } catch (_e) {
      return null;
    }
  })();

  const [userOverride, setUserOverride] = useState(initialOverride);
  const [leagueDefault, setLeagueDefault] = useState(
    isValidBoardStyle(leagueDefaultFromState) ? leagueDefaultFromState : null,
  );

  // Keep leagueDefault in sync with the incoming draftState value.
  useEffect(() => {
    if (isValidBoardStyle(leagueDefaultFromState)) {
      setLeagueDefault(leagueDefaultFromState);
    }
  }, [leagueDefaultFromState]);

  // Reconcile user override with backend truth on mount / league change.
  useEffect(() => {
    if (!leagueId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${BACKEND}/api/users/me/board-preference?league_id=${encodeURIComponent(leagueId)}`,
          { headers: authHeaders() },
        );
        if (cancelled) return;
        const serverVal = res?.data?.board_style;
        const norm = isValidBoardStyle(serverVal) ? serverVal : null;
        setUserOverride(norm);
        // Reconcile localStorage in case server wins.
        try {
          if (norm) {
            localStorage.setItem(boardStyleLocalStorageKey(leagueId), norm);
          } else {
            localStorage.removeItem(boardStyleLocalStorageKey(leagueId));
          }
        } catch (_e) { /* ignore */ }
      } catch (_e) { /* non-fatal - warm cache stays */ }
    })();
    return () => { cancelled = true; };
  }, [leagueId]);

  const style = resolveBoardStyle(userOverride, leagueDefault) || DEFAULT_BOARD_STYLE;

  // Selector calls this for immediate re-render before the network reply.
  const setLocalStyle = useCallback((next, asOverride) => {
    if (asOverride) {
      setUserOverride(isValidBoardStyle(next) ? next : null);
    } else {
      setLeagueDefault(isValidBoardStyle(next) ? next : null);
    }
  }, []);

  return { style, userOverride, leagueDefault, setLocalStyle };
}
